import React, { useState } from 'react';
import { 
  BriefcaseIcon,
  HandRaisedIcon,
  UserGroupIcon,
  RocketLaunchIcon 
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

const JoinOurTeam = () => {
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [showGeneralApplication, setShowGeneralApplication] = useState(false);

  const benefits = [
    {
      icon: <BriefcaseIcon className="w-12 h-12" />,
      title: 'Competitive Salary',
      description: 'We offer industry-leading compensation packages'
    },
    {
      icon: <HandRaisedIcon className="w-12 h-12" />,
      title: 'Work-Life Balance',
      description: 'Flexible working hours and remote options'
    },
    {
      icon: <UserGroupIcon className="w-12 h-12" />,
      title: 'Great Culture',
      description: 'Collaborative and inclusive workplace'
    },
    {
      icon: <RocketLaunchIcon className="w-12 h-12" />,
      title: 'Growth Opportunities',
      description: 'Career development and learning programs'
    }
  ];

  const openPositions = [
    {
      title: 'Senior Software Engineer',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time'
    },
    {
      title: 'Product Designer',
      department: 'Design',
      location: 'New York',
      type: 'Full-time'
    },
    {
      title: 'Marketing Manager',
      department: 'Marketing',
      location: 'Remote',
      type: 'Full-time'
    }
  ];

  const handleApply = (position) => {
    setSelectedPosition(position.title);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      {/* Hero Section */}
      <div className="relative bg-primary dark:bg-primary-dark text-white py-32 overflow-hidden">
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="container mx-auto px-6 relative z-10 text-center"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6">Join Our Team</h1>
          <p className="text-xl md:text-2xl max-w-2xl mx-auto text-gray-200 font-light">
            Be part of something special. We're looking for talented individuals to help us shape the future.
          </p>
        </motion.div>
      </div>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16 text-gray-900 dark:text-white">Why Work With Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white dark:bg-dark-card p-8 rounded-lg transition-all duration-300 text-center border border-gray-200 dark:border-gray-700"
              >
                <div className="text-secondary dark:text-secondary-light mb-6 flex justify-center">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">{benefit.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section className="py-20 bg-white dark:bg-dark-card transition-colors duration-300">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16 text-gray-900 dark:text-white">Open Positions</h2>
          <div className="max-w-4xl mx-auto">
            {openPositions.map((position, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="bg-gray-50 dark:bg-dark-bg p-6 rounded-lg mb-4 border border-gray-200 dark:border-gray-700 hover:border-secondary dark:hover:border-secondary transition-all duration-300">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">{position.title}</h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {position.department} • {position.location} • {position.type}
                      </p>
                    </div>
                    <button 
                      onClick={() => handleApply(position)}
                      className="bg-primary dark:bg-primary-light text-white dark:text-primary-dark px-6 py-2 rounded-lg border border-primary font-medium hover:bg-secondary dark:hover:bg-secondary transition-colors duration-300 whitespace-nowrap"
                    >
                      Apply Now
                    </button>
                  </div>
                </div>
                {selectedPosition === position.title && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="bg-primary/5 dark:bg-white/5 p-6 rounded-lg mb-6 text-center border border-primary/10 dark:border-white/10"
                  >
                    <p className="text-gray-700 dark:text-gray-300">
                      To apply for <span className="font-semibold text-gray-900 dark:text-white">{position.title}</span>, 
                      please send your CV to <span className="text-primary dark:text-secondary-light font-bold">careers@afroawi.com</span>
                      <br />
                      <span className="text-sm mt-2 block opacity-80">
                        Email subject: "Job Application - {position.title}"
                      </span>
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-100 dark:bg-dark-bg border-t border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">Don't See the Right Fit?</h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-10 max-w-2xl mx-auto">
            We're always looking for talented individuals to join our team.
            Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <div>
            <button 
              onClick={() => setShowGeneralApplication(true)}
              className="bg-primary dark:bg-primary-light text-white dark:text-primary-dark px-8 py-3 rounded-lg border border-primary text-lg font-bold hover:bg-secondary dark:hover:bg-secondary transition-colors duration-300"
            >
              Send Your Resume
            </button>
            {showGeneralApplication && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-dark-card p-6 rounded-lg mt-8 shadow-md max-w-2xl mx-auto border border-gray-100 dark:border-gray-700"
              >
                <p className="text-gray-700 dark:text-gray-300">
                  For general applications, please send your CV to{' '}
                  <span className="text-primary dark:text-secondary-light font-bold">careers@afroawi.com</span>
                  <br />
                  <span className="text-sm mt-2 block opacity-80">
                    Email subject: "General Application - Open Position"
                  </span>
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default JoinOurTeam;
